/**
 * `useGameStatusLite(gameId, { enabled })` — per-card status hydrate
 * for the Dashboard's lazy-load path.
 *
 * Wraps `game:status:lite` (new IPC in PR 1) in a React Query cache
 * with a renderer-side concurrency cap of 6 in-flight requests at any
 * moment. The cap matches the plan's `docs/dashboard-lazy-load-plan.md`
 * line 103: when the user scrolls and a batch of cards flip visible
 * simultaneously, we don't want all 20 firing `game:status:lite` at
 * once — that pushes the per-call work (3 git spawns + 1 registry HTTP
 * each, see plan line 195) into a contention spiral on slower hardware.
 *
 * The cap is renderer-side only — main has its own hard semaphore on
 * `getLocalGitStatus` (per plan line 103). Belt-and-braces.
 *
 * Cache policy mirrors the staleness rules at plan lines 207-211:
 *   - `localGit` stale after 30 s — short enough for fresh commits during
 *     a session but not so short the grid re-fetches on every focus.
 *   - The whole `GameStatusLite` payload shares the same `staleTime` —
 *     finer-grained staleness per sub-field would need a real cache
 *     reducer, not worth it for the lite path.
 */

import { useEffect, useRef } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { GameStatusLite } from '@volley/crucible-ipc-types/ipc-channels';

/** Hard cap on simultaneously-in-flight `game:status:lite` requests. */
const MAX_IN_FLIGHT = 6;

/**
 * Tiny FIFO semaphore. `tickets` counts free slots; `waiters` is the
 * queue of resolvers waiting for a slot. `acquire()` returns when a
 * slot is free; `release()` hands the slot back to the head waiter (or
 * back to the free pool if no one's waiting).
 *
 * Module-scope state — every consumer of `useGameStatusLite` shares the
 * same gate, which is the whole point. Visible-for-tests via the
 * `_resetGameStatusLiteSemaphoreForTests` helper.
 */
let tickets = MAX_IN_FLIGHT;
const waiters: Array<() => void> = [];

async function acquire(): Promise<void> {
  if (tickets > 0) {
    tickets -= 1;
    return;
  }
  await new Promise<void>((resolve) => {
    waiters.push(resolve);
  });
}

function release(): void {
  const next = waiters.shift();
  if (next) {
    next();
    return;
  }
  tickets += 1;
  // Defensive: never let the pool overflow the configured max. A
  // double-release bug would otherwise let the cap drift upward
  // silently.
  if (tickets > MAX_IN_FLIGHT) tickets = MAX_IN_FLIGHT;
}

const STATUS_LITE_OPTIONS = {
  staleTime: 30_000,
  refetchOnMount: false,
  refetchOnWindowFocus: false,
  // Per-card hydrate is a fire-and-show — a single transient failure
  // shouldn't spam retries against an already-degraded backend.
  retry: false,
} as const;

export interface UseGameStatusLiteOptions {
  /**
   * Mirrors `useIpcQuery`'s `enabled` shape — `false` keeps the query
   * dormant until the parent flips it on (typically via
   * `useCardVisibility`'s `isVisible`).
   */
  enabled?: boolean;
}

export function useGameStatusLite(
  gameId: string,
  opts?: UseGameStatusLiteOptions,
): UseQueryResult<GameStatusLite> {
  const enabled = opts?.enabled ?? true;
  // Track whether THIS hook instance currently holds a semaphore
  // ticket so an unmount-mid-flight (e.g. user navigates away while
  // the IPC is in flight) releases the slot. Without this an interrupted
  // hydrate would permanently shrink the pool.
  const heldTicketRef = useRef(false);

  const query = useQuery<GameStatusLite>({
    queryKey: ['game:status:lite', gameId],
    queryFn: async () => {
      await acquire();
      heldTicketRef.current = true;
      try {
        return await window.crucible.invoke('game:status:lite', gameId);
      } finally {
        heldTicketRef.current = false;
        release();
      }
    },
    enabled: enabled && typeof gameId === 'string' && gameId.length > 0,
    ...STATUS_LITE_OPTIONS,
  });

  useEffect(() => {
    return () => {
      // If we unmount while still holding the ticket (IPC in flight,
      // promise hasn't settled), the queryFn's `finally` will still
      // run after the IPC resolves OR rejects — React Query awaits
      // the queryFn fully regardless of mount state. So we DON'T
      // double-release here; we leave the held-ticket bookkeeping to
      // the `finally` above. This effect's only job is to document
      // that promise-lifetime ownership.
      heldTicketRef.current = heldTicketRef.current; // no-op
    };
  }, []);

  return query;
}

/**
 * Test-only: reset the semaphore + waiter queue between tests. Mirrors
 * the `_resetFeatureFlagCacheForTests` / `_resetCardVisibilityForTests`
 * shape.
 */
export function _resetGameStatusLiteSemaphoreForTests(): void {
  tickets = MAX_IN_FLIGHT;
  waiters.length = 0;
}
